import { Box, useMediaQuery, useTheme } from '@mui/material';
import { DataGridPro, GRID_CHECKBOX_SELECTION_FIELD } from '@mui/x-data-grid-pro';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import SelectionActionBar from '@sophocles/components/SelectionActionBar';
import { MOBILE_MIN_ROW_HEIGHT, resolveRowHeightProps } from '@sophocles/data-grids/components/mobile';
import DataGridToolbar, { GridSettingsButton } from '@sophocles/data-grids/components/toolbar';

const EMPTY_SELECTION = { type: 'include', ids: new Set() };

// The row-selection checkbox column must never be hideable from the columns
// panel — losing it strands a grid's selection UI with no way to get it back
// short of a hard refresh. MUI's built-in checkbox col def doesn't set
// hideable: false itself, so every ServerDataGrid (checkboxSelection or not)
// excludes it from the panel's togglable list centrally, rather than each
// grid remembering to do so.
const getTogglableColumns = columns =>
  columns.filter(col => col.field !== GRID_CHECKBOX_SELECTION_FIELD).map(col => col.field);

// Border, header background, row hover, focus outlines all come from
// the global MuiDataGrid theme override in theme.js. Per-component sx
// only carries the bits that are wrapper-specific.
//
// `minWidth: 0` is what keeps a wide table inside the page. A flex item's
// automatic minimum size is its content, so without it the grid's total column
// width — which a drag-resize grows on purpose — becomes the floor its parent
// must honour, and the whole layout widens past the viewport instead of the
// grid scrolling within it.
const baseSx = {
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  width: '100%',
};

const desktopBaseSx = {
  // `display: 'flex'` per MUI X v7+ — required for `alignItems: 'center'`
  // to actually vertically center renderCell content. Columns that
  // explicitly set their own `display` still win via column-level config.
  '& .MuiDataGrid-cell': {
    display: 'flex',
    alignItems: 'center',
    py: 0.5,
  },
};

// Zebra striping + tinted anchor strip (pinned name col + checkbox col).
// Toggleable via the `zebra` prop; on by default. Pinned ID col, when
// present, stays uniform white so a flipping row anchor doesn't read busy.
//
// We key zebra off a stable .row-odd class (assigned via getRowClassName
// from `indexRelativeToCurrentPage`) instead of CSS `:nth-of-type(odd)` —
// DataGridPro virtualizes rows, so DOM nth-of-type flips as rows enter
// and leave the viewport, making the stripes appear to shift while
// scrolling. Indexing off the data position keeps each row's parity
// stable regardless of which slice is rendered.
//
// The stripe paints the ROW, never the cells: cells sit on top of the row,
// so a cell-level stripe would mask every row-level background — the theme's
// row hover and semantic row classes (e.g. a failed-command tint). Row-level
// styles on the same element resolve by cascade instead: a caller's semantic
// class wins by matching the stripe's specificity (`& .MuiDataGrid-row.row--x`
// — caller sx merges after this object). Pinned/checkbox anchor cells are the
// one exception: they need an opaque cell paint so scrolling columns can't
// show through them.
const desktopZebraSx = {
  '& .MuiDataGrid-row.row-odd': {
    backgroundColor: 'var(--grid-row-alt-bg-tinted)',
  },
  // The theme's plain `.MuiDataGrid-row:hover` rule loses the cascade to the
  // stripe above, so hover re-asserts itself at stripe-beating specificity.
  '& .MuiDataGrid-row.row-odd:hover': {
    backgroundColor: 'var(--grid-row-hover)',
  },
  '& .MuiDataGrid-row.row-odd.Mui-selected': {
    backgroundColor: 'var(--grid-row-selected)',
  },
  '& .MuiDataGrid-row.row-odd.Mui-selected:hover': {
    backgroundColor: 'var(--grid-row-selected-hover)',
  },
  '& .MuiDataGrid-row .MuiDataGrid-cell.cell-pinned, & .MuiDataGrid-row .MuiDataGrid-cell[data-field="__check__"]': {
    backgroundColor: 'var(--grid-name-cell-bg)',
  },
  '& .MuiDataGrid-row.row-odd .MuiDataGrid-cell.cell-pinned, & .MuiDataGrid-row.row-odd .MuiDataGrid-cell[data-field="__check__"]':
    {
      backgroundColor: 'var(--grid-name-cell-bg-alt)',
    },
  '& .MuiDataGrid-row:hover .MuiDataGrid-cell.cell-pinned, & .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field="__check__"]':
    {
      backgroundColor: 'var(--grid-row-hover)',
    },
  '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell.cell-pinned, & .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell[data-field="__check__"]':
    {
      backgroundColor: 'var(--grid-row-selected)',
    },
  '& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell.cell-pinned, & .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell[data-field="__check__"]':
    {
      backgroundColor: 'var(--grid-row-selected-hover)',
    },
};

// Opt-in pointer affordance for grids whose rows open a detail
// slideout on click (warehouse Products/Orders). Link-navigating grids
// already signal clickability via the linked cell, so this is off by default.
const clickableRowSx = {
  '& .MuiDataGrid-row': { cursor: 'pointer' },
};

// Card-mode rows are cards, not cells: they measure themselves (see
// resolveRowHeightProps) and are free to grow. Applied only when the caller
// actually collapsed its columns to cards (`mobileCards`) — hiding headers on
// a grid still rendering its column set leaves every column past the first
// unlabeled and unreachable. The cell keeps a minimum height so an unmeasured
// skeleton row cannot collapse — a lazy grid's next-page fetch only fires
// while the scroller still overflows.
//
// Width is pinned at every level the grid can widen from. A card's own content
// stays inside its column (its flex children carry minWidth: 0), but a
// horizontally scrolling list is the failure this closes off for good.
const mobileRowSx = {
  '& .MuiDataGrid-columnHeaders': { display: 'none' },
  '& .MuiDataGrid-row': {
    width: '100%',
    maxWidth: '100%',
    '&:hover': { backgroundColor: 'transparent' },
  },
  '& .MuiDataGrid-cell': {
    borderBottom: 'none',
    p: 0,
    overflow: 'visible',
    whiteSpace: 'normal',
    maxHeight: 'none !important',
    minHeight: MOBILE_MIN_ROW_HEIGHT,
    maxWidth: '100%',
  },
  '& .MuiDataGrid-virtualScroller': { overflowX: 'hidden' },
};

/**
 * Thin wrapper around DataGridPro that wires up the common server-side
 * pattern used across all entity grids (Sites, SiteGroups, Customers).
 *
 * Selection:
 *   checkboxSelection   – boolean, enables checkbox column
 *   selectionActions     – render prop: (selection, clearSelection) => ReactNode
 *                          `selection` is { ids: number[], selectAll: boolean }
 *   selectAllActive      – boolean, true when all rows across all pages are selected
 *   onSelectAllRequest   – callback fired when the header checkbox selects all;
 *                          parent sets selectAllActive=true immediately (no dialog)
 *   onSelectAllClear     – callback fired when selection is cleared
 *
 * Mobile:
 *   mobileCards          – boolean; the caller has collapsed `columns` to a
 *                          single self-measuring card column. Card chrome
 *                          (hidden headers, no horizontal scroll, auto row
 *                          heights) applies only then — a phone-width grid
 *                          still showing its column set keeps headers and
 *                          scrolls horizontally instead.
 *
 * All other DataGridPro props can be passed via ...rest.
 */
export default function ServerDataGrid({
  gridState,
  columns,
  rows,
  onRowClick,
  toolbarContent,
  toolbarActions,
  createAction,
  searchPlaceholder,
  showSearch = true,
  showGridSettings = true,
  onConfigure,
  searchSlot,
  countNoun,
  toolbarVariant = 'elevated',
  checkboxSelection = false,
  selectionActions,
  selectAllActive = false,
  onSelectAllRequest,
  onSelectAllClear,
  zebra = true,
  clickableRows = false,
  mobileCards = false,
  sx: sxOverride,
  children,
  getRowClassName: getRowClassNameOverride,
  ...rest
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isCardMode = isMobile && mobileCards;

  const [selectionModel, setSelectionModel] = useState(EMPTY_SELECTION);

  const selectedIds = useMemo(() => Array.from(selectionModel.ids || []), [selectionModel]);

  const clearSelection = useCallback(() => {
    setSelectionModel(EMPTY_SELECTION);
    if (onSelectAllClear) onSelectAllClear();
  }, [onSelectAllClear]);

  // A new dataSource means a different result set, so the rows already on
  // screen belong to the previous one. Lazy loading omits rowCount to stay in
  // infinite-loading mode, which leaves the grid no way to work out that the
  // new result is shorter. Without an explicit reset the old rows survive the
  // swap and a narrowed filter looks like it did nothing.
  //
  // Cache-clear and refetch are NOT done here: MUI's own dataSource-prop
  // effect (useGridDataSourceBase) already clears the cache and calls
  // fetchRows() on this same identity change. Duplicating that races two
  // fetches — the second bumps the request id, so the first's response is
  // silently dropped.
  const hasBoundDataSourceRef = useRef(false);
  useEffect(() => {
    setSelectionModel(EMPTY_SELECTION);
    if (!hasBoundDataSourceRef.current) {
      hasBoundDataSourceRef.current = true;
      return;
    }
    const api = gridState.apiRef?.current;
    if (!api?.dataSource) return;
    api.setRows?.([]);
  }, [gridState.dataSource, gridState.apiRef]);

  // When parent activates select-all, apply the "all rows" model to the grid
  useEffect(() => {
    if (selectAllActive) {
      setSelectionModel({ type: 'exclude', ids: new Set() });
    }
  }, [selectAllActive]);

  const handleSelectionChange = useCallback(
    newModel => {
      // Detect "select all" — MUI sends type: 'exclude' with empty ids
      if (newModel.type === 'exclude' && (newModel.ids?.size ?? 0) === 0) {
        if (onSelectAllRequest) {
          onSelectAllRequest(gridState.totalRowCount);
          return; // Parent sets selectAllActive, which triggers the useEffect above
        }
      }
      // If deselecting while select-all is active, clear it
      if (selectAllActive && newModel.type === 'include') {
        if (onSelectAllClear) onSelectAllClear();
      }
      setSelectionModel(newModel);
    },
    [onSelectAllRequest, onSelectAllClear, selectAllActive, gridState.totalRowCount],
  );

  const selection = useMemo(() => ({ ids: selectedIds, selectAll: selectAllActive }), [selectedIds, selectAllActive]);

  const displayCount = selectAllActive ? (gridState.totalRowCount ?? 0) : selectedIds.length;

  // In-memory mode: caller supplies a pre-built rows array (e.g. a live Redux
  // slice). Gate is strict `!== undefined` so an empty [] is valid ("no rows").
  // Server mode (rows === undefined): all server-side props are passed through
  // unchanged — the DataGridPro is functionally identical to the pre-modes code.
  const isInMemory = rows !== undefined;
  const dataProps = isInMemory
    ? { rows, loading: gridState.loading }
    : {
        dataSource: gridState.dataSource,
        lazyLoading: gridState.lazyLoading,
        sortingMode: 'server',
        sortModel: gridState.sortModel,
        onSortModelChange: gridState.setSortModel,
        filterMode: 'server',
        filterModel: gridState.filterModel,
        onFilterModelChange: gridState.onFilterModelChange,
        loading: gridState.loading,
      };

  const gridSettingsButton =
    showGridSettings && !isMobile ? (
      <GridSettingsButton
        tooltip={t('general.gridSettings', 'Grid settings')}
        onClick={onConfigure ?? (() => gridState.apiRef?.current?.showPreferences?.('columns'))}
      />
    ) : null;

  const composedToolbarActions =
    gridSettingsButton || toolbarActions ? (
      <>
        {toolbarActions}
        {gridSettingsButton}
      </>
    ) : null;

  const showToolbar = Boolean(toolbarContent || composedToolbarActions || createAction || showSearch);

  return (
    // minWidth: 0 for the same reason as baseSx — this Box is itself a flex
    // item, and a wrapper that grows to its content hands the grid's width
    // straight on to the page.
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        width: '100%',
        position: 'relative',
      }}
    >
      {children}
      {showToolbar && (
        <DataGridToolbar
          apiRef={gridState.apiRef}
          variant={toolbarVariant}
          showQuickFilter={showSearch}
          quickFilterPlaceholder={searchPlaceholder}
          searchSlot={searchSlot}
          toolbarContent={toolbarContent}
          toolbarActions={composedToolbarActions}
          createAction={createAction}
          totalRowCount={isInMemory ? null : gridState.totalRowCount}
          countNoun={countNoun}
        />
      )}
      <DataGridPro
        apiRef={gridState.apiRef}
        getRowId={gridState.getRowId}
        {...resolveRowHeightProps({ lazyLoading: gridState.lazyLoading, isMobile: isCardMode })}
        columns={columns}
        disableColumnFilter
        disableRowSelectionOnClick
        checkboxSelection={checkboxSelection}
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={handleSelectionChange}
        hideFooter
        slotProps={{
          // First (empty) load shows a column-aware skeleton; a fetch that
          // already has rows uses a non-blocking top progress bar. The
          // skeleton overlay replaces the whole grid body — on a lazy-scroll
          // append that remounts the scroller and snaps scroll back to the top.
          loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' },
          columnsManagement: { getTogglableColumns },
        }}
        localeText={{ noRowsLabel: t('status.noResults') }}
        onRowClick={onRowClick}
        getRowClassName={params => {
          const callerClass = getRowClassNameOverride?.(params) ?? '';
          // indexRelativeToCurrentPage is the row's data-index (stable
          // across virtualization), unlike DOM :nth-of-type which flips
          // as rows enter/leave the viewport.
          const zebraClass = zebra && !isCardMode && params.indexRelativeToCurrentPage % 2 ? 'row-odd' : '';
          return [callerClass, zebraClass].filter(Boolean).join(' ');
        }}
        sx={{
          ...baseSx,
          ...(isCardMode ? mobileRowSx : { ...desktopBaseSx, ...(zebra ? desktopZebraSx : {}) }),
          ...(clickableRows && !isCardMode ? clickableRowSx : {}),
          ...sxOverride,
        }}
        {...dataProps}
        {...rest}
      />
      {selectionActions && (
        <SelectionActionBar count={displayCount} onClear={clearSelection} selectAll={selectAllActive}>
          {selectionActions(selection, clearSelection)}
        </SelectionActionBar>
      )}
    </Box>
  );
}
